import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
  BugAntIcon,
  CheckIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  MoonIcon,
  ShieldCheckIcon,
  SunIcon,
  SwatchIcon,
  UserCircleIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import type { Locale, Theme, UserRole } from '@xfile/shared';
import { useOutsideGestureDismiss } from '../components/dismiss-gesture';
import { labelWithDetail, useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { useTheme } from '../theme';
import { useHealth } from '../lib/useHealth';
import { Portal } from '../components/Portal';
import { BugReportModal } from '../diagnostics/BugReportModal';
import { useAuth } from './AuthProvider';
import { useIsSuperadmin } from './useIsSuperadmin';

/** Clé i18n du libellé de rôle. */
const ROLE_LABEL: Record<UserRole, MessageKey> = {
  superadmin: 'auth.roleSuperadmin',
  admin: 'auth.roleAdmin',
  user: 'auth.roleUser',
};

/** Clé i18n du libellé de thème (aria-label du bouton radio). */
const THEME_LABEL: Record<Theme, MessageKey> = {
  light: 'theme.light',
  dark: 'theme.dark',
  system: 'theme.system',
};

/** Icône du contrôle segmenté de thème. */
const THEME_ICON: Record<Theme, React.ReactNode> = {
  light: <SunIcon className="h-5 w-5" aria-hidden="true" />,
  dark: <MoonIcon className="h-5 w-5" aria-hidden="true" />,
  system: <ComputerDesktopIcon className="h-5 w-5" aria-hidden="true" />,
};

/**
 * Version affichée dans le footer : `version+build` (sha git) pour aider au debug
 * et distinguer deux builds au même SemVer. Le `+build` est masqué en dev (buildId
 * absent ou `'dev'`).
 */
function versionLabel(version: string, buildId: string): string {
  return buildId && buildId !== 'dev' ? `${version}+${buildId}` : version;
}

/** Style commun d'une entrée d'action (bouton pleine largeur). */
const ROW =
  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none';

/**
 * Menu de compte (multi-login XFILE-14) déclenché par l'engrenage de l'en-tête.
 *
 * Panneau déroulant (popover étiqueté, PAS `role="menu"` — il contient un
 * radiogroup de thème et un select de langue, incompatibles avec `menu`) :
 * comptes empilés (actif marqué par ✓ + `aria-checked`, jamais la couleur seule),
 * bascule sans re-login, ajout de compte, contrôle Thème/Langue inline, modale
 * « Signaler un bug » (XFILE-13), « Se déconnecter » (tous les comptes, confirmation
 * en deux temps) et footer version via `/api/health`.
 * Accessible : Échap ferme (focus rendu au déclencheur), clic extérieur ferme.
 */
export function AccountSwitcher(): React.JSX.Element | null {
  const { t, locale, setLocale, locales } = useI18n();
  const { theme, setTheme, themes } = useTheme();
  const { user, accounts, activeSlot, switchTo, logout, addAccount } = useAuth();
  const isSuperadmin = useIsSuperadmin();
  const { health } = useHealth();
  const [open, setOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    setConfirmingAll(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  // Geste extérieur ENTIER → fermeture (cf. `useOutsideGestureDismiss`).
  const closeWithoutFocus = useCallback(() => close(false), [close]);
  useOutsideGestureDismiss(open, rootRef, closeWithoutFocus);

  useEffect(() => {
    if (!open) return;
    // Échap ferme quelle que soit la position du focus (déclencheur inclus).
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!user) return null;

  const onLogoutAll = (): void => {
    if (!confirmingAll) {
      setConfirmingAll(true);
      return;
    }
    void logout(activeSlot ?? accounts[0]?.slot ?? 0, true);
    close(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('auth.account')}
        title={t('auth.account')}
        onClick={() => setOpen((o) => !o)}
        className={`flex h-11 w-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
          open ? 'bg-surface-2 text-fg outline-2 outline-offset-2 outline-brand' : ''
        }`}
      >
        <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      {open && (
        <div
          aria-label={t('accountSwitcher.title')}
          className="absolute end-0 z-20 mt-1 min-w-72 rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          {/* 1. Titre */}
          <p id="account-switcher-title" className="px-3 py-2 text-xs font-medium text-muted">
            {t('accountSwitcher.title')}
          </p>

          {/* 2. Comptes empilés (le compte actif = radio coché, jamais la couleur seule) */}
          <div role="radiogroup" aria-labelledby="account-switcher-title">
            {accounts.map((account) => {
              const isActive = account.slot === activeSlot;
              return (
                <div key={account.slot} className="flex items-stretch gap-1">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    title={account.email}
                    onClick={() => {
                      if (!isActive) switchTo(account.slot);
                      close(false);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
                  >
                    <UserCircleIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {account.displayName}
                    </span>
                    <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-xs uppercase tracking-wide text-muted">
                      {t(ROLE_LABEL[account.role])}
                    </span>
                    {isActive ? (
                      <CheckIcon
                        className="h-5 w-5 shrink-0 text-brand-ink"
                        aria-label={t('accountSwitcher.active')}
                      />
                    ) : (
                      <span className="h-5 w-5 shrink-0" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={labelWithDetail(
                      t,
                      t('accountSwitcher.logoutAccount'),
                      account.email,
                    )}
                    title={t('accountSwitcher.logoutAccount')}
                    onClick={() => void logout(account.slot)}
                    className="flex w-9 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-danger focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
                  >
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* 3. Ajouter un compte */}
          <button
            type="button"
            onClick={() => {
              addAccount();
              close(false);
            }}
            className={`${ROW} text-fg`}
          >
            <UserPlusIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('accountSwitcher.addAccount')}</span>
          </button>

          {/* 4. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 5. Liens : console superadmin (masquée hors superadmin) + Signaler un bug. */}
          {isSuperadmin && (
            <Link to="/admin" onClick={() => close(false)} className={`${ROW} text-fg`}>
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
              <span>{t('admin.navConsole')}</span>
            </Link>
          )}

          {/* Signaler un bug (XFILE-13) — modale locale, non routée. */}
          <button
            type="button"
            onClick={() => {
              setReportOpen(true);
              close(false);
            }}
            className={`${ROW} text-fg`}
          >
            <BugAntIcon className="h-5 w-5" aria-hidden="true" />
            <span>{t('bugReport.open')}</span>
          </button>

          {/* 6. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 7. Thème (contrôle segmenté) */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-fg">
            <SwatchIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            <span className="flex-1">{t('accountSwitcher.theme')}</span>
            <div
              role="radiogroup"
              aria-label={t('accountSwitcher.theme')}
              className="flex items-center gap-1 rounded-md border border-border p-0.5"
            >
              {themes.map((th) => {
                const selected = th === theme;
                return (
                  <button
                    key={th}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={t(THEME_LABEL[th])}
                    title={t(THEME_LABEL[th])}
                    onClick={() => setTheme(th as Theme)}
                    className={`flex h-8 w-8 items-center justify-center rounded transition-colors focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none ${
                      selected ? 'bg-brand-strong text-brand-fg' : 'text-muted hover:bg-surface-2'
                    }`}
                  >
                    {THEME_ICON[th]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 6. Langue (select natif) */}
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-fg">
            <GlobeAltIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
            <span className="flex-1">{t('accountSwitcher.language')}</span>
            <select
              aria-label={t('accountSwitcher.language')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              className="h-9 rounded-md border border-border-strong bg-surface px-2 text-fg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
            >
              {locales.map((l) => (
                <option key={l} value={l}>
                  {t(`lang.${l}`)}
                </option>
              ))}
            </select>
          </div>

          {/* 8. Séparateur */}
          <div className="my-1 border-t border-border" />

          {/* 9. Se déconnecter (tous les comptes, confirmation en deux temps) */}
          <button type="button" onClick={onLogoutAll} className={`${ROW} text-danger`}>
            <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
            <span>{confirmingAll ? t('accountSwitcher.confirmLogoutAll') : t('auth.signOut')}</span>
          </button>

          {/* 11. Footer version (avec le sha git : aide au debug + signale un
              nouveau build même sans bump SemVer). `+build` masqué en dev. */}
          {health && (
            <div className="border-t border-border px-3 py-2 text-xs text-muted">
              {`${health.name} ${versionLabel(health.version, health.buildId)} · ${health.publicHostname}`}
            </div>
          )}
        </div>
      )}
      {/* « Signaler un bug » reste une modale locale (non routée) portée vers <body>
          via Portal — s'ouvre en plein écran, indépendamment de la fermeture du menu. */}
      {reportOpen && (
        <Portal>
          <BugReportModal onClose={() => setReportOpen(false)} />
        </Portal>
      )}
    </div>
  );
}
